import React, { useCallback, useEffect, useState } from 'react';

import {
    Badge,
    Button,
    ButtonGroup,
    Card,
    ChoiceList,
    DatePicker,
    Filters,
    IndexTable,
    InlineStack,
    Page,
    Select,
    Text,
    TextField,
    useIndexResourceState,
} from '@shopify/polaris';

import usePos from '../hooks/Pos';
import usePosApi from '../hooks/Api';
import { useNavigate } from 'react-router-dom';

// Format the order's UTC timestamp in the device's local timezone (matches Shopify POS).
const formatOrderDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d)
        ? iso
        : d.toLocaleString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
          });
};

function PosOrderListingPage() {
    const { request } = usePosApi();
    const navigate = useNavigate();

    const [orders, setOrders] = useState([]);
    const [month, setMonth] = useState(new Date().getMonth());
    const [year, setYear] = useState(new Date().getFullYear());
    const [selectedDates, setSelectedDates] = useState(null);
    const [orderName, setOrderName] = useState('');
    const debouncedOrderName = useDebounce(orderName, 1000);
    const [selectedLocation, setSelectedLocation] = useState('');
    const [locations, setLocations] = useState([]);
    const [locationFilterOptions, setLocationFilterOptions] = useState([]);
    const [appliedFilters, setAppliedFilters] = useState([]);
    const [loading, setLoading] = useState(false);
    const [shouldLoadMore, setShouldLoadMore] = useState(false);
    const [cursor, setCursor] = useState(null);
    const [error, setError] = useState(null);
    const [locationConfig, setLocationConfig] = useState({ allow_consolidation: true });

    const { selectedResources, allResourcesSelected, handleSelectionChange } = useIndexResourceState(orders);

    async function loadData(afterCursor = null) {
        let url = 'orders';
        const params = new URLSearchParams();

        if (selectedDates) {
            params.append('start_date', selectedDates.start.toISOString().split('T')[0]);
            params.append('end_date', selectedDates.end.toISOString().split('T')[0]);
        }

        if (selectedLocation) {
            params.append('location', selectedLocation);
        }

        if (orderName) {
            params.append('name', orderName);
        }

        if (afterCursor) {
            params.append('cursor', afterCursor);
        }

        if (params.toString()) {
            url += `?${params.toString()}`;
        }

        setLoading(true);
        try {
            const { data: response } = await request(url);
            setShouldLoadMore(response.pageInfo.hasNextPage);
            setCursor(response.pageInfo.endCursor);
            if (afterCursor) {
                setOrders(orders.concat(response.orders));
            } else {
                setOrders(response.orders);
            }
        } catch (error) {
            setError(error);
        } finally {
            setLoading(false);
        }
    }

    async function loadLocationConfig() {
        try {
            const { data } = await request('config');
            setLocationConfig(data);
        } catch (error) {
            console.error('Failed to load location config:', error);
        }
    }

    async function loadLocations() {
        try {
            const { data } = await request('locations');
            setLocations(data);
        } catch (error) {
            console.error('Failed to load locations:', error);
        }
    }

    function useDebounce(value, delay) {
        const [debouncedValue, setDebouncedValue] = useState(value);

        useEffect(() => {
            // Set a timeout to update the debounced value
            const timer = setTimeout(() => {
                setDebouncedValue(value);
            }, delay);

            // Cleanup function to clear the timeout if the value changes again before the delay
            return () => {
                clearTimeout(timer);
            };
        }, [value, delay]); // Only re-run if value or delay changes

        return debouncedValue;
    }

    useEffect(() => {
        setLocationFilterOptions([
            { label: 'Current Location', value: 'current' },
            { label: 'All Locations', value: 'all' },
            ...locations.map((location) => {
                return {
                    label: location.name,
                    value: location.id,
                };
            }),
        ]);
    }, [locations]);

    useEffect(() => {
        shopify.loading(true);
        Promise.all([loadData(), loadLocationConfig(), loadLocations()]).then(() => {
            shopify.loading(false);
        });
    }, []);

    const handleMonthChange = useCallback((month, year) => {
        setMonth(month);
        setYear(year);
    }, []);

    const handleLocationChange = useCallback((value) => {
        setSelectedLocation(value);
    }, []);

    const handleOrderNameChange = useCallback((value) => {
        setOrderName(value);
    }, []);

    const handleClearFilters = useCallback(() => {
        setSelectedDates(null);
        setSelectedLocation('');
        setOrderName('');
    }, []);

    const filters = [
        {
            key: 'dates',
            label: 'Between Dates',
            filter: (
                <DatePicker
                    onChange={setSelectedDates}
                    selected={selectedDates}
                    month={month}
                    year={year}
                    onMonthChange={handleMonthChange}
                    allowRange
                    multiMonth
                />
            ),
            shortcut: true,
        },
        {
            key: 'location',
            label: 'Location',
            filter: (
                <Select
                    label="Location"
                    options={locationFilterOptions}
                    value={selectedLocation}
                    onChange={handleLocationChange}
                />
            ),
            hideClearButton: true,
        },
        {
            key: 'name',
            label: 'Order name',
            filter: (
                <TextField
                    label="Order name"
                    value={orderName}
                    onChange={handleOrderNameChange}
                    autoComplete="off"
                    labelHidden
                />
            ),
            shortCut: true,
        },
    ];

    useEffect(() => {
        const applied = [];

        if (selectedDates) {
            applied.push({
                key: 'dates',
                label: `Between ${selectedDates.start.toLocaleDateString()} and ${selectedDates.end.toLocaleDateString()}`,
                onRemove: () => setSelectedDates(null),
            });
        }

        if (selectedLocation) {
            applied.push({
                key: 'location',
                label: locationFilterOptions.find((filterOption) => filterOption.value == selectedLocation).label,
                onRemove: () => setSelectedLocation(''),
            });
        }

        if (orderName) {
            applied.push({
                key: 'name',
                label: `Order name: ${orderName}`,
                onRemove: () => setOrderName(''),
            });
        }

        setCursor(null);
        setAppliedFilters(applied);
        loadData();
    }, [selectedDates, selectedLocation, debouncedOrderName]);

    const promotedBulkActions = locationConfig.allow_consolidation
        ? [
              {
                  content: 'Issue Tax-Free Form',
                  onAction: () => navigate(`/orders/form?ids=${selectedResources.join(',')}&source=list`),
              },
          ]
        : [];

    return (
        <Page backAction={{ content: 'Index', onAction: () => navigate(-1) }} title="Orders List">
            <Card padding="0">
                <Filters
                    filters={filters}
                    appliedFilters={appliedFilters}
                    hideQueryField
                    onClearAll={handleClearFilters}
                />
                <IndexTable
                    selectable={locationConfig.allow_consolidation}
                    resourceName={{
                        singular: 'order',
                        plural: 'orders',
                    }}
                    itemCount={orders.length}
                    headings={[
                        { title: 'Order Name' },
                        { title: 'Date' },
                        { title: 'Customer' },
                        { title: 'Tax-Free Status' },
                        { title: 'Actions', alignment: 'end' },
                    ]}
                    loading={loading}
                    onSelectionChange={handleSelectionChange}
                    selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
                    promotedBulkActions={promotedBulkActions}
                >
                    {orders.map(({ id, name, date, taxFreeForm, customer }, index) => (
                        <IndexTable.Row
                            id={id}
                            key={id}
                            selected={selectedResources.includes(id)}
                            position={index}
                            disabled={['complete'].includes(taxFreeForm?.status)}
                        >
                            <IndexTable.Cell>
                                <Text variant="bodyMd" fontWeight="bold" as="span">
                                    {name}
                                </Text>
                            </IndexTable.Cell>
                            <IndexTable.Cell>
                                <Text>{formatOrderDate(date)}</Text>
                            </IndexTable.Cell>
                            <IndexTable.Cell>
                                <Text>{customer}</Text>
                            </IndexTable.Cell>
                            <IndexTable.Cell>
                                {taxFreeForm?.status === 'complete' && (
                                    <Badge tone="success" progress="complete">
                                        Complete
                                    </Badge>
                                )}
                                {taxFreeForm?.status === 'voided' && (
                                    <Badge tone="critical" progress="complete">
                                        Voided
                                    </Badge>
                                )}
                                {taxFreeForm?.status === 'open' && (
                                    <Badge tone="attention" progress="incomplete">
                                        Pending
                                    </Badge>
                                )}
                                {!taxFreeForm?.status && (
                                    <Badge tone="new" progress="incomplete">
                                        Available
                                    </Badge>
                                )}
                            </IndexTable.Cell>
                            <IndexTable.Cell>
                                <InlineStack align="end">
                                    {taxFreeForm?.status === 'complete' ? (
                                        <Button
                                            variant="secondary"
                                            onClick={() =>
                                                navigate(`/pre-issued-forms/${taxFreeForm.planet_form_id}/details`)
                                            }
                                        >
                                            View
                                        </Button>
                                    ) : (
                                        <ButtonGroup>
                                            {taxFreeForm?.status === 'voided' && (
                                                <Button
                                                    variant="secondary"
                                                    onClick={() =>
                                                        navigate(
                                                            `/pre-issued-forms/${taxFreeForm.planet_form_id}/details`,
                                                        )
                                                    }
                                                >
                                                    View
                                                </Button>
                                            )}
                                            <Button
                                                variant="secondary"
                                                onClick={() => navigate(`/orders/form?ids=${id}&source=list`)}
                                            >
                                                {taxFreeForm?.status === 'voided'
                                                    ? 'Re-Issue'
                                                    : taxFreeForm?.status === 'open'
                                                      ? 'Complete Form'
                                                      : 'Issue Form'}
                                            </Button>
                                        </ButtonGroup>
                                    )}
                                </InlineStack>
                            </IndexTable.Cell>
                        </IndexTable.Row>
                    ))}
                </IndexTable>
                {shouldLoadMore && (
                    <Button
                        fullWidth
                        variant="monocromePlain"
                        disclosure="down"
                        size="large"
                        onClick={() => loadData(cursor)}
                    >
                        Load More
                    </Button>
                )}
            </Card>
        </Page>
    );
}

export default PosOrderListingPage;
